// @ts-check

// Vendor imports
const React = require('react')
const ReactRedux = require('react-redux')
// Shared imports
const { ExtendableError } = require('shared/error-utils')
const {
  CanceledPromiseError,
  makePromiseCancelable
} = require('shared/promise-utils')
// Client lib imports
const airbrake = require('client/lib/airbrake')
// Client V2 common imports
const tag = require('client-v2/common/utils/hyperscript')
// Sibling imports
const { fetchSvg, gatherSymbolIds } = require('./utils')

const context = React.createContext({
  /** @type {string[] | null} */
  iconIds: null
})

/**
 * React Component that provides the Icons context.
 * @param {Object} props
 * @param {React.ElementType} props.children
 */
function IconsManager(props) {
  // @ts-ignore Redux state currently does not have type annotations
  const iconsUrl = ReactRedux.useSelector(state => state.env.iconsUrl)
  const iconIds = useIconsLoader(iconsUrl)
  return (
    tag(context.Provider, {
      value: { iconIds }
    },
      props.children
    )
  )
}

if (__DEV__) {
  // Prop-checking imports
  const PropTypes = require('prop-types')

  IconsManager.propTypes = {
    children: PropTypes.node
  }
}

/**
 * React hook to load icons SVG and inject it into the DOM
 * @param {string} iconsUrl
 * @returns {string[] | null} loaded icon IDs
 */
function useIconsLoader(iconsUrl) {
  const [ iconIds, setIconIds ] = React.useState(null)
  React.useEffect(
    () => {
      // Do nothing during server-side rendering
      if (typeof document === 'undefined') {
        return
      }

      const parentEl = document.body
      const fetchPromise = fetchSvg(iconsUrl)
      const promiseWrapper = makePromiseCancelable(fetchPromise)

      /** @type {SVGElement} */
      let svgEl

      promiseWrapper.promise
      .then(result => {
        svgEl = result
        setIconIds(gatherSymbolIds(svgEl))
        parentEl.insertBefore(svgEl, parentEl.firstChild)
      })
      .catch(err => {
        if (!(err instanceof CanceledPromiseError)) {
          airbrake.notify(err)
        }
      })

      return function cleanupLoaderEffect() {
        if (svgEl) {
          parentEl.removeChild(svgEl)
        } else {
          promiseWrapper.cancel()
        }
        setIconIds(null)
      }
    },
    [ iconsUrl ]
  )
  return iconIds
}

class IconMissingWarning extends ExtendableError {
  /**
   * @param {string} iconId
   */
  constructor(iconId) {
    super(`Usage of missing icon: ${iconId}`)
  }
}

/**
 * React hook to verify that `iconId` is a usable icon
 * @param {string} iconId
 * @returns {void}
 */
function useIconWarning(iconId) {
  const { iconIds } = React.useContext(context)
  React.useEffect(
    () => {
      if (iconIds && !iconIds.includes(iconId)) {
        airbrake.notify(new IconMissingWarning(iconId))
      }
    },
    [ iconIds, iconId ]
  )
}

module.exports = {
  // Components
  IconsManager,
  // Hooks
  useIconWarning
}
